// /src/shared/components/MysticalLoader/index.jsx
import React from 'react';
import { Box, Typography, styled } from '@mui/material';
import { keyframes } from '@mui/system';
import PropTypes from 'prop-types';

// Define animation keyframes
const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.2); opacity: 0.8; }
  100% { transform: scale(1); opacity: 1; }
`;

const rotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const sparkle = keyframes`
  0% { opacity: 0; transform: scale(0.4) rotate(0deg); }
  50% { opacity: 1; transform: scale(1) rotate(180deg); }
  100% { opacity: 0; transform: scale(0.4) rotate(360deg); }
`;

// Styled components
const LoaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '200px',
  width: '100%',
  position: 'relative',
}));

const LampContainer = styled(Box)(({ theme, size }) => ({
  position: 'relative',
  width: size === 'small' ? 60 : size === 'medium' ? 80 : 120,
  height: size === 'small' ? 80 : size === 'medium' ? 100 : 140,
  animation: `${floatAnimation} 3s ease-in-out infinite`,
}));

const Lamp = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 120'%3E%3Cpath d='M30,100 L70,100 L65,80 L35,80 Z' fill='%23D9AD1F'/%3E%3Cpath d='M35,80 L65,80 L60,65 L40,65 Z' fill='%23F2C526'/%3E%3Cpath d='M40,65 L60,65 C70,55 75,40 75,30 C75,15 60,5 50,5 C40,5 25,15 25,30 C25,40 30,55 40,65 Z' fill='%23FFD859'/%3E%3Cpath d='M38,100 L62,100 L64,110 L36,110 Z' fill='%23B08E1A'/%3E%3C/svg%3E")`,
  backgroundSize: 'contain',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  position: 'relative',
  zIndex: 2,
}));

const LampGlow = styled(Box)(({ theme, isGlowing, color }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '120%',
  height: '120%',
  borderRadius: '50%',
  background: `radial-gradient(circle, ${color || '#f2c526'}80 0%, rgba(255,255,255,0) 70%)`,
  opacity: 0.5,
  animation: `${pulse} 2s ease-in-out infinite`,
  zIndex: 1,
}));

const MagicSmoke = styled(Box)(({ theme, color }) => ({
  position: 'absolute',
  top: '10%',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '80%',
  height: '50%',
  background: `radial-gradient(circle, ${color || '#0D2E4D'}60 0%, rgba(255,255,255,0) 70%)`,
  borderRadius: '50%',
  filter: 'blur(15px)',
  zIndex: 0,
  opacity: 0.6,
  animation: `${pulse} 4s ease-in-out infinite`,
}));

const MagicCircle = styled(Box)(({ theme, size, color }) => ({
  position: 'absolute',
  top: '0%',
  left: '50%',
  transform: 'translateX(-50%)',
  width: size === 'small' ? 100 : size === 'medium' ? 150 : 200,
  height: size === 'small' ? 30 : size === 'medium' ? 40 : 50,
  borderRadius: '50%',
  border: `2px solid ${color || '#f2c526'}`,
  opacity: 0.7,
  zIndex: 3,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '95%',
    height: '90%',
    borderRadius: '50%',
    border: `1px solid ${color || '#0D2E4D'}`,
  },
  animation: `${rotate} 10s linear infinite`,
}));

const Sparkle = styled(Box)(({ theme, delay, size, color, top, left }) => ({
  position: 'absolute',
  top: top || '20%',
  left: left || '20%',
  width: size === 'small' ? 10 : size === 'medium' ? 15 : 20,
  height: size === 'small' ? 10 : size === 'medium' ? 15 : 20,
  background: `radial-gradient(circle, ${color || '#FFD859'} 0%, rgba(255,255,255,0) 70%)`,
  borderRadius: '50%',
  zIndex: 4,
  animation: `${sparkle} 2s ease-in-out infinite`,
  animationDelay: `${delay || 0}s`,
}));

const LoadingText = styled(Typography)(({ theme }) => ({
  marginTop: 24,
  color: '#f2c526',
  fontWeight: 500,
  letterSpacing: '0.05em',
  textAlign: 'center',
}));

// The MysticalLoader component
const MagicLoading = ({
  size = 'medium',
  text = 'Summoning magic...',
  color,
  variant = 'lamp',
  fullPage = false,
}) => {
  // Generate multiple sparkles with random positions
  const renderSparkles = () => {
    const sparkles = [];
    const positions = [
      { top: '10%', left: '20%' },
      { top: '30%', left: '80%' },
      { top: '60%', left: '30%' },
      { top: '20%', left: '60%' },
      { top: '70%', left: '70%' },
      { top: '50%', left: '20%' },
    ];
    
    for (let i = 0; i < 6; i++) {
      sparkles.push(
        <Sparkle 
          key={i}
          size={size}
          delay={i * 0.3}
          color={color}
          top={positions[i].top}
          left={positions[i].left}
        />
      );
    }
    
    return sparkles;
  };
  
  return (
    <LoaderContainer
      sx={fullPage ? {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(7, 23, 37, 0.9)',
        zIndex: 9999,
        minHeight: '100vh',
      } : {}}
    >
      <LampContainer size={size}>
        <MagicCircle size={size} color={color} />
        <Lamp />
        <LampGlow color={color || '#f2c526'} />
        <MagicSmoke color={color || '#0D2E4D'} />
        {renderSparkles()}
      </LampContainer>
      
      {text && (
        <LoadingText variant={size === 'small' ? 'body2' : 'body1'}>
          {text}
        </LoadingText>
      )}
    </LoaderContainer>
  );
};

MagicLoading.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  text: PropTypes.string,
  color: PropTypes.string,
  variant: PropTypes.oneOf(['lamp', 'portal']),
  fullPage: PropTypes.bool,
};

export default MagicLoading;